import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { AppButton, AppTextField } from "../ui";
import "./UploadElementDialog.scss";

const SUPPORTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  ".obj",
  ".fbx",
  ".pdb",
  ".zip",
  ".glb",
  ".gltf",
  ".swc",
];

const FILE_INPUT_ACCEPT = "image/png, image/jpeg, .obj, .fbx, .pdb, .zip, .glb, .gltf, .swc";
const SUPPORTED_FILE_HELPER_TEXT = "Supported file types: FBX, OBJ, JPG, PNG, PDB, ZIP, GLB, GLTF, SWC";

const isFileSupported = (file) => {
  if (file.type && (file.type === "image/png" || file.type === "image/jpeg")) {
    return true;
  }

  const dotIndex = file.name.lastIndexOf(".");
  if (dotIndex < 0) {
    return false;
  }

  const extension = file.name.slice(dotIndex).toLowerCase();
  return SUPPORTED_FILE_TYPES.includes(extension);
};

const getFileKey = (file) => `${file.name}::${file.size}::${file.lastModified}`;

const BulkUploadElementDialog = ({ open, onClose, onUpload }) => {
  const [namePrefix, setNamePrefix] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setNamePrefix("");
    setSelectedFiles([]);
    setIsDragActive(false);
  };

  const addFiles = (fileList) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const incomingFiles = Array.from(fileList);
    const supportedFiles = incomingFiles.filter((file) => isFileSupported(file));
    const unsupportedFiles = incomingFiles.filter((file) => !isFileSupported(file));

    if (unsupportedFiles.length > 0) {
      const unsupportedNames = unsupportedFiles.map((file) => file.name).join(", ");
      alert(`Unsupported file type for: ${unsupportedNames}. ${SUPPORTED_FILE_HELPER_TEXT}`);
    }

    if (supportedFiles.length === 0) {
      return;
    }

    setSelectedFiles((prev) => {
      const dedupedFiles = new Map();
      [...prev, ...supportedFiles].forEach((file) => {
        dedupedFiles.set(getFileKey(file), file);
      });
      return Array.from(dedupedFiles.values());
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget === e.target) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleClickDropzone = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0 || loading) {
      return;
    }

    setLoading(true);
    try {
      await onUpload({
        namePrefix,
        files: selectedFiles,
      });
      resetState();
    } catch (error) {
      console.error("Bulk upload failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    resetState();
    onClose();
  };

  const fileSummaryText =
    selectedFiles.length === 0
      ? "Drag and drop files here, or click to select files"
      : `${selectedFiles.length} files selected`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "upload-element-dialog__paper" }}
    >
      <DialogTitle className="upload-element-dialog__title">Bulk Upload Elements</DialogTitle>
      <DialogContent className="upload-element-dialog__content">
        <Grid container spacing={2} className="upload-element-dialog__form">
          <Grid item xs={12}>
            <AppTextField
              label="Name Prefix"
              value={namePrefix}
              onChange={(e) => setNamePrefix(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClickDropzone}
              className={`upload-element-dialog__dropzone ${isDragActive ? "upload-element-dialog__dropzone--active" : ""}`}
            >
              <Typography className="upload-element-dialog__dropzone-text">
                {fileSummaryText}
              </Typography>
              <input
                type="file"
                accept={FILE_INPUT_ACCEPT}
                multiple
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
            </Box>
            {selectedFiles.length > 0 && (
              <div className="upload-element-dialog__file-list">
                {selectedFiles.map((file) => (
                  <Typography key={getFileKey(file)} className="upload-element-dialog__file-item">
                    {file.name}
                  </Typography>
                ))}
              </div>
            )}
            <Typography variant="caption" className="upload-element-dialog__naming-help">
              Element names use `prefix + filename`; description uses the filename.
            </Typography>
            <Typography variant="caption" className="upload-element-dialog__helper-text">
              {SUPPORTED_FILE_HELPER_TEXT}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="upload-element-dialog__actions">
        <AppButton
          kind="ghost"
          onClick={handleClose}
          disabled={loading}
          className="upload-element-dialog__cancel"
        >
          Cancel
        </AppButton>
        <AppButton
          onClick={handleUploadClick}
          loading={loading}
          loadingLabel={`Uploading ${selectedFiles.length} files`}
          disabled={selectedFiles.length === 0}
        >
          {selectedFiles.length > 0
            ? `Create & Upload ${selectedFiles.length} Elements`
            : "Create & Upload Elements"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadElementDialog;
